/**
 * Smart Prospect Question System - Question Tracker
 * 
 * Manages tracking of recently shown questions using localStorage
 * to ensure variety and prevent repetition within a 15-question window.
 */

interface QuestionHistory {
  questionIds: number[];      // Recently shown question IDs (max 15)
  timestamps: number[];       // When each question was shown (as Unix timestamps)
  sessionStart: number;       // Current session start time
  totalShown: number;         // Total questions shown (lifetime counter)
}

interface SessionStats {
  questionsThisSession: number;
  sessionDuration: number;     // in minutes
  uniqueQuestions: number;
  repeatedQuestions: number;
}

class QuestionTracker {
  private static instance: QuestionTracker;
  private readonly STORAGE_KEY = 'blp-prospect-questions-history';
  private readonly BACKUP_STORAGE_KEY = 'blp-prospect-questions-backup';
  private readonly MAX_RECENT_QUESTIONS = 15;
  private readonly HISTORY_RETENTION_HOURS = 24;
  private readonly MAX_STORAGE_SIZE_KB = 10;

  private constructor() {}

  static getInstance(): QuestionTracker {
    if (!QuestionTracker.instance) {
      QuestionTracker.instance = new QuestionTracker();
    }
    return QuestionTracker.instance;
  }

  /**
   * Get list of recently shown question IDs to exclude from selection
   */
  getRecentQuestionIds(maxRecent: number = this.MAX_RECENT_QUESTIONS): number[] {
    try {
      const history = this.loadFromStorage();
      
      // Clean old history first
      this.clearOldHistory(this.HISTORY_RETENTION_HOURS);
      
      // Return most recent question IDs up to the limit
      return history.questionIds.slice(0, maxRecent);
    } catch (error) {
      this.logError('Failed to get recent question IDs', error as Error);
      return [];
    }
  }

  /**
   * Add a question to the tracking history
   */
  addQuestionToHistory(questionId: number): void {
    try {
      const history = this.loadFromStorage();
      const now = Date.now();
      
      // Add new question to the front of the list
      history.questionIds.unshift(questionId);
      history.timestamps.unshift(now);
      history.totalShown++;
      
      // Maintain maximum size (keep only last N questions)
      if (history.questionIds.length > this.MAX_RECENT_QUESTIONS) {
        history.questionIds = history.questionIds.slice(0, this.MAX_RECENT_QUESTIONS);
        history.timestamps = history.timestamps.slice(0, this.MAX_RECENT_QUESTIONS);
      }
      
      // Clean old entries
      this.clearOldHistory(this.HISTORY_RETENTION_HOURS, history);
      
      // Save updated history
      this.saveToStorage(history);
      
      // Log selection for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log(`[QuestionTracker] Added question ${questionId} to history. Recent count: ${history.questionIds.length}`);
      }
    } catch (error) {
      this.logError('Failed to add question to history', error as Error);
    }
  }

  /**
   * Clear history older than specified hours
   */
  clearOldHistory(maxAgeHours: number = this.HISTORY_RETENTION_HOURS, history?: QuestionHistory): void {
    try {
      const historyData = history || this.loadFromStorage();
      const cutoffTime = Date.now() - (maxAgeHours * 60 * 60 * 1000);
      
      const validIndices = historyData.timestamps
        .map((timestamp: number, index: number) => ({ timestamp, index }))
        .filter(item => item.timestamp > cutoffTime)
        .map(item => item.index);
      
      if (validIndices.length < historyData.questionIds.length) {
        historyData.questionIds = validIndices.map(i => historyData.questionIds[i]);
        historyData.timestamps = validIndices.map(i => historyData.timestamps[i]);
        
        if (!history) {
          // Only save if we weren't passed history directly
          this.saveToStorage(historyData);
        }
      }
    } catch (error) {
      this.logError('Failed to clear old history', error as Error);
    }
  }

  /**
   * Reset the current session (but keep historical data)
   */
  resetSession(): void {
    try {
      const history = this.loadFromStorage();
      history.sessionStart = Date.now();
      this.saveToStorage(history);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[QuestionTracker] Session reset');
      }
    } catch (error) {
      this.logError('Failed to reset session', error as Error);
    }
  }

  /**
   * Get statistics about the current session
   */
  getSessionStats(): SessionStats {
    try {
      const history = this.loadFromStorage();
      const now = Date.now();
      const sessionStart = history.sessionStart || now;
      const sessionDuration = Math.round((now - sessionStart) / (1000 * 60)); // minutes
      
      // Count questions shown this session
      const sessionQuestions = history.timestamps.filter(
        timestamp => timestamp >= sessionStart
      );
      
      // Count unique vs repeated questions this session
      const sessionQuestionIds = history.questionIds.slice(0, sessionQuestions.length);
      const uniqueSessionQuestions = new Set(sessionQuestionIds);
      
      return {
        questionsThisSession: sessionQuestions.length,
        sessionDuration: Math.max(sessionDuration, 0),
        uniqueQuestions: uniqueSessionQuestions.size,
        repeatedQuestions: sessionQuestions.length - uniqueSessionQuestions.size
      };
    } catch (error) {
      this.logError('Failed to get session stats', error as Error);
      return {
        questionsThisSession: 0,
        sessionDuration: 0,
        uniqueQuestions: 0,
        repeatedQuestions: 0
      };
    }
  }

  /**
   * Load history from localStorage with error handling
   */
  loadFromStorage(): QuestionHistory {
    try {
      // Try primary storage
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.validateAndUpgradeHistory(parsed);
      }
      
      // Try backup storage
      const backup = localStorage.getItem(this.BACKUP_STORAGE_KEY);
      if (backup) {
        const parsed = JSON.parse(backup);
        const validated = this.validateAndUpgradeHistory(parsed);
        // Restore to primary storage
        this.saveToStorage(validated);
        return validated;
      }
      
      // Return default history
      return this.createDefaultHistory();
    } catch (error) {
      this.logError('Failed to load from storage', error as Error);
      return this.createDefaultHistory();
    }
  }

  /**
   * Save history to localStorage with backup and size management
   */
  saveToStorage(history: QuestionHistory): void {
    try {
      const serialized = JSON.stringify(history);
      const sizeKB = new Blob([serialized]).size / 1024;
      
      if (sizeKB > this.MAX_STORAGE_SIZE_KB) {
        // Reduce history size if too large
        const reducedHistory = this.reduceHistorySize(history);
        this.saveToStorage(reducedHistory);
        return;
      }
      
      // Create backup of current data
      const currentData = localStorage.getItem(this.STORAGE_KEY);
      if (currentData) {
        localStorage.setItem(this.BACKUP_STORAGE_KEY, currentData);
      }
      
      // Save new data
      localStorage.setItem(this.STORAGE_KEY, serialized);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[QuestionTracker] Saved history: ${Math.round(sizeKB * 100) / 100}KB`);
      }
    } catch (error) {
      const err = error as Error;
      if (err.name === 'QuotaExceededError') {
        // Storage quota exceeded - reduce history size
        try {
          const reducedHistory = this.reduceHistorySize(history);
          const reducedSerialized = JSON.stringify(reducedHistory);
          localStorage.setItem(this.STORAGE_KEY, reducedSerialized);
          this.logError('Storage quota exceeded, reduced history size', err);
        } catch (retryError) {
          this.logError('Failed to save even reduced history', retryError as Error);
          this.clearStorage(); // Last resort
        }
      } else {
        this.logError('Failed to save to storage', err);
      }
    }
  }

  /**
   * Clear all stored history (emergency reset)
   */
  clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.BACKUP_STORAGE_KEY);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[QuestionTracker] Cleared all storage');
      }
    } catch (error) {
      this.logError('Failed to clear storage', error as Error);
    }
  }

  /**
   * Create default/empty history object
   */
  private createDefaultHistory(): QuestionHistory {
    return {
      questionIds: [],
      timestamps: [],
      sessionStart: Date.now(),
      totalShown: 0
    };
  }

  /**
   * Validate and upgrade history format if needed
   */
  private validateAndUpgradeHistory(data: any): QuestionHistory {
    // Handle legacy formats or corrupted data
    if (!data || typeof data !== 'object') {
      return this.createDefaultHistory();
    }
    
    const history: QuestionHistory = {
      questionIds: Array.isArray(data.questionIds) ? data.questionIds.filter((id: any) => typeof id === 'number') : [],
      timestamps: Array.isArray(data.timestamps) ? data.timestamps.filter((ts: any) => typeof ts === 'number') : [],
      sessionStart: typeof data.sessionStart === 'number' ? data.sessionStart : Date.now(),
      totalShown: typeof data.totalShown === 'number' ? data.totalShown : 0
    };
    
    // Ensure arrays are same length
    const minLength = Math.min(history.questionIds.length, history.timestamps.length);
    history.questionIds = history.questionIds.slice(0, minLength);
    history.timestamps = history.timestamps.slice(0, minLength);
    
    return history;
  }

  /**
   * Reduce history size when storage limits are reached
   */
  private reduceHistorySize(history: QuestionHistory): QuestionHistory {
    const maxSize = Math.floor(this.MAX_RECENT_QUESTIONS / 2); // Keep half the normal size
    
    return {
      ...history,
      questionIds: history.questionIds.slice(0, maxSize),
      timestamps: history.timestamps.slice(0, maxSize)
    };
  }

  /**
   * Log errors with context
   */
  private logError(message: string, error: Error): void {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[QuestionTracker] ${message}:`, error);
    }
    
    // In production, could send to error tracking service
    // errorTrackingService.captureException(error, { context: message });
  }

  /**
   * Check if question was recently shown
   */
  isQuestionRecent(questionId: number, withinCount: number = this.MAX_RECENT_QUESTIONS): boolean {
    try {
      const recent = this.getRecentQuestionIds(withinCount);
      return recent.includes(questionId);
    } catch (error) {
      this.logError('Failed to check if question is recent', error as Error);
      return false; // Assume not recent on error
    }
  }

  /**
   * Get debug information about the tracker state
   */
  getDebugInfo(): Record<string, any> {
    try {
      const history = this.loadFromStorage();
      const stats = this.getSessionStats();
      const storageSize = localStorage.getItem(this.STORAGE_KEY)?.length || 0;
      
      return {
        historyLength: history.questionIds.length,
        totalShown: history.totalShown,
        sessionStats: stats,
        storageSizeBytes: storageSize,
        storageSizeKB: Math.round((storageSize / 1024) * 100) / 100,
        recentQuestions: history.questionIds.slice(0, 5), // Show first 5
        oldestTimestamp: history.timestamps[history.timestamps.length - 1],
        newestTimestamp: history.timestamps[0]
      };
    } catch (error) {
      return { error: (error as Error).message };
    }
  }
}

// Export singleton instance
export const questionTracker = QuestionTracker.getInstance();

// Export types for external use
export type { QuestionHistory, SessionStats };